import { z } from "zod";

/**
 * Google Calendar, as ontrack's server and devices exchange it (ADR 0027, 0029). The Worker calls
 * Google with the User's own calendar grant and trims every event to these fields; devices cache
 * them and expand repeating events themselves, as they do with Events.
 */

/** The access ontrack asks Google for: events, and the list of calendars to choose from. */
export const GOOGLE_CALENDAR_SCOPES = [
  "https://www.googleapis.com/auth/calendar.events",
  "https://www.googleapis.com/auth/calendar.calendarlist.readonly"
] as const;

/** The private extended properties ontrack keeps on a Google event: its Project and Task. */
export const GOOGLE_PROJECT_KEY = "otrackProject";
export const GOOGLE_TASK_KEY = "otrackTask";

const text = (max: number) => z.string().max(max);

/** `date` for all-day events (the end is exclusive), `dateTime` with `timeZone` for timed ones. */
export const googleTimeSchema = z.object({
  date: text(10).optional(),
  dateTime: text(40).optional(),
  timeZone: text(100).optional()
});
export type GoogleTime = z.infer<typeof googleTimeSchema>;

const personSchema = z.object({
  email: text(320).optional(),
  displayName: text(200).optional(),
  self: z.boolean().optional(),
  organizer: z.boolean().optional(),
  responseStatus: text(40).optional()
});

export const googleEventSchema = z.object({
  id: text(1024),
  status: z.enum(["confirmed", "tentative", "cancelled"]).catch("confirmed").default("confirmed"),
  summary: text(2000).optional(),
  description: text(16_000).optional(),
  location: text(2000).optional(),
  start: googleTimeSchema.optional(),
  end: googleTimeSchema.optional(),
  recurrence: z.array(text(2000)).max(50).optional(),
  recurringEventId: text(1024).optional(),
  originalStartTime: googleTimeSchema.optional(),
  updated: text(40).optional(),
  htmlLink: text(2000).optional(),
  hangoutLink: text(2000).optional(),
  eventType: text(40).optional(),
  guestsCanModify: z.boolean().optional(),
  organizer: personSchema.optional(),
  attendees: z.array(personSchema).max(100).optional(),
  extendedProperties: z.object({ private: z.record(z.string(), text(1024)).optional() }).optional()
});
export type GoogleEvent = z.infer<typeof googleEventSchema>;

export const googleCalendarSchema = z.object({
  id: text(1024),
  name: text(300),
  /** Google's own colour for the calendar, as #rrggbb. */
  color: text(20),
  primary: z.boolean(),
  /** "owner" and "writer" may change events; "reader" and "freeBusyReader" may only see them. */
  accessRole: text(40)
});
export type GoogleCalendar = z.infer<typeof googleCalendarSchema>;
export const googleCalendarsResponseSchema = z.object({ calendars: z.array(googleCalendarSchema) });

export const googleStatusSchema = z.object({
  /** "connected", "disconnected" (never granted), "reconnect" (granted once, no longer works), "unavailable" (not set up here). */
  state: z.enum(["connected", "disconnected", "reconnect", "unavailable"]),
  email: z.string().nullable()
});
export type GoogleStatus = z.infer<typeof googleStatusSchema>;

/** Where Google's consent sends the browser back: the web app, or a page pointing to the desktop app. */
export const googleConnectRequestSchema = z.object({ returnTo: z.enum(["web", "desktop"]) });
export type GoogleConnectRequest = z.infer<typeof googleConnectRequestSchema>;
/** Google's consent page for this User, valid for a few minutes. */
export const googleConnectResponseSchema = z.object({ url: z.string().url() });

export const googleEventsResponseSchema = z.object({
  events: z.array(googleEventSchema),
  /** Asks for the changes after this point next time; null when Google gave none. */
  syncToken: z.string().nullable(),
  /** The sync token expired: drop the cache for this calendar and fetch it all again. */
  reset: z.boolean()
});
export type GoogleEventsResponse = z.infer<typeof googleEventsResponseSchema>;

/** Fields a device may write. Null clears a field, which Google's merge-on-patch needs for start and end. */
const writeTimeSchema = z.object({
  date: text(10).nullable().optional(),
  dateTime: text(40).nullable().optional(),
  timeZone: text(100).nullable().optional()
});
export const googleWriteSchema = z.object({
  summary: text(2000).optional(),
  description: text(16_000).optional(),
  location: text(2000).optional(),
  start: writeTimeSchema.optional(),
  end: writeTimeSchema.optional(),
  recurrence: z.array(text(2000)).max(50).optional(),
  status: z.enum(["confirmed", "tentative", "cancelled"]).optional(),
  extendedProperties: z.object({ private: z.record(z.string(), text(1024)) }).optional()
});
export type GoogleWrite = z.infer<typeof googleWriteSchema>;

/** Event ids ontrack creates: Google allows base32hex (0-9, a-v), 5 to 1024 characters. */
export const googleIdSchema = z.string().regex(/^[0-9a-v]{5,1024}$/);

/**
 * One change to send to Google. Inserts carry their own id, so sending one twice is harmless.
 * A patch of an event deleted in Google is refused as "gone" unless it `restores` the event (Undo
 * of a delete); a restore that Google can't apply recreates the event from its fields instead.
 */
export const googleChangeSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("insert"), calendarId: text(1024), eventId: googleIdSchema, fields: googleWriteSchema }),
  z.object({ type: z.literal("patch"), calendarId: text(1024), eventId: text(1100), fields: googleWriteSchema, restore: z.boolean().optional() }),
  z.object({ type: z.literal("delete"), calendarId: text(1024), eventId: text(1100) }),
  /** Moves an event (a whole series) to another of the User's calendars. */
  z.object({ type: z.literal("move"), calendarId: text(1024), eventId: text(1100), destination: text(1024) })
]);
export type GoogleChange = z.infer<typeof googleChangeSchema>;

export const googleApplyRequestSchema = z.object({ changes: z.array(googleChangeSchema).min(1).max(25) });

export const googleChangeResultSchema = z.discriminatedUnion("ok", [
  /** The event as Google now has it; absent for a delete. */
  z.object({ ok: z.literal(true), event: googleEventSchema.nullable() }),
  /**
   * gone: deleted in Google, so the change was dropped. refused: Google won't take it (no access,
   * invalid). Both are final. retry: Google or the connection failed; later changes were not sent.
   */
  z.object({ ok: z.literal(false), reason: z.enum(["gone", "refused", "retry", "reconnect"]), message: z.string() })
]);
export type GoogleChangeResult = z.infer<typeof googleChangeResultSchema>;

export const googleApplyResponseSchema = z.object({ results: z.array(googleChangeResultSchema) });
